import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ModulePage";
import StatusBadge from "@/components/StatusBadge";

export default function BusinessValue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/business-value").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const raCtx = "Business value center. Metrics tracked with baseline, target and actual: " + rows.slice(0, 6).map((r) => `${r.metric} actual ${r.actual} versus target ${r.target}`).join(", ") + ". All figures are simulated.";

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  return (
    <div>
      <PageHeader title="Business Value Center" subtitle="Baseline → Target → Actual with evidence. Figures are SIMULATED, never fabricated ROI claims." readAloudContext={raCtx} actions={<StatusBadge value="SIMULATED" />} />
      <Card className="mb-6" testId="value-chart">
        <h3 className="mb-3 text-sm font-semibold">Expected vs Realized Value (simulated)</h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="metric" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-25} textAnchor="end" height={80} />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="baseline" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
            <Bar dataKey="target" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
            <Bar dataKey="actual" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground">{["Metric", "Baseline", "Target", "Actual", "Variance", "Status"].map((h) => <th key={h} className="px-3 py-2 text-start font-semibold">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.metric} className="border-b border-border/50">
                <td className="px-3 py-2.5 font-medium">{r.metric}</td>
                <td className="px-3 py-2.5">{r.baseline}{r.unit}</td>
                <td className="px-3 py-2.5">{r.target}{r.unit}</td>
                <td className="px-3 py-2.5">{r.actual}{r.unit}</td>
                <td className={`px-3 py-2.5 font-mono ${r.variance >= 0 ? "text-emerald-300" : "text-amber-300"}`}>{r.variance >= 0 ? "+" : ""}{r.variance}</td>
                <td className="px-3 py-2.5"><StatusBadge value="OBSERVED" /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
